/**
 * Authentication context and provider for managing Azure AD authentication state
 * Wraps MSAL functionality for easy consumption throughout the React app
 */

import React, { createContext, useContext, useEffect, useState } from 'react';
import { 
  PublicClientApplication, 
  AuthenticationResult,
  AccountInfo,
  InteractionStatus,
  EventType,
  EventMessage,
  AuthError
} from '@azure/msal-browser';
import { 
  MsalProvider,
  useMsal,
  useAccount,
  useIsAuthenticated 
} from '@azure/msal-react';
import { msalConfig, loginRequest, tokenRequest } from './authConfig';
import { ChatAPI } from '../utils/api';
import { trackAuthEvent, trackException } from '../utils/appInsights';
import type { ApiUser } from '../models/types';

// Create MSAL instance with singleton pattern to prevent multiple instances
declare global {
  interface Window {
    msalInstance?: PublicClientApplication;
  }
}

export const getMsalInstance = (): PublicClientApplication => {
  if (window.msalInstance) {
    console.log('[Auth] Reusing existing MSAL instance from window');
    return window.msalInstance;
  }
  
  console.log('[Auth] Creating new MSAL instance');
  const instance = new PublicClientApplication(msalConfig);
  window.msalInstance = instance;
  return instance;
};

export const msalInstance = getMsalInstance();

// Note: MsalProvider will handle initialization automatically

// Authentication context interface
interface AuthContextType {
  isAuthenticated: boolean;
  user: AccountInfo | null;
  userClaims: any | null; // JWT claims including roles
  apiUser: ApiUser | null;
  apiUserLoading: boolean;
  apiUserError: string | null;
  loginInProgress: boolean;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  getToken: () => Promise<string | null>;
  error: string | null;
  errorType: 'generic' | 'access_denied' | null;
}

/**
 * Helper function to parse Azure AD error messages and determine error type
 */
const parseAzureADError = (error: any): { message: string; type: 'generic' | 'access_denied' } => {
  const errorMessage = error.message || error.errorMessage || 'Authentication failed';
  const errorCode = error.errorCode || '';
  const errorDescription = error.error_description || '';
  
  // Combine all error text for searching
  const fullErrorText = `${errorMessage} ${errorCode} ${errorDescription}`.toLowerCase();
  
  // Check for AADSTS50105 - User not assigned to role
  if (fullErrorText.includes('aadsts50105')) {
    return {
      message: 'You are not assigned to this application. Please contact your administrator to request the appropriate role assignment.',
      type: 'access_denied'
    };
  }
  
  // Check for other access denied scenarios
  if (fullErrorText.includes('user_cancelled') || fullErrorText.includes('user_canceled')) {
    // This often happens when user closes the popup due to access denied
    return {
      message: 'Sign-in was cancelled. If you received an access denied message, please contact your administrator for access.',
      type: 'access_denied'
    };
  }
  
  // Check for assignment required errors
  if (fullErrorText.includes('assignment required') || 
      fullErrorText.includes('not assigned') || 
      fullErrorText.includes('blocked because they are not a direct member')) {
    return {
      message: 'You are not assigned to this application. Please contact your administrator to request access.',
      type: 'access_denied'
    };
  }
  
  // Check for insufficient privileges
  if (fullErrorText.includes('insufficient privileges') || 
      fullErrorText.includes('access denied') ||
      fullErrorText.includes('forbidden')) {
    return {
      message: 'Insufficient privileges to access this application. Please contact your administrator.',
      type: 'access_denied'
    };
  }
  
  // Generic error handling
  return {
    message: errorMessage,
    type: 'generic'
  };
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Custom hook to use authentication context
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * Authentication provider component that wraps the app
 */
interface AuthProviderProps {
  children: React.ReactNode;
}

const AuthProviderInner: React.FC<AuthProviderProps> = ({ children }) => {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const account = useAccount(accounts[0] || {});
  
  const [loginInProgress, setLoginInProgress] = useState(false);
  const [autoSigninAttempted, setAutoSigninAttempted] = useState(false);
  const [userSignedOut, setUserSignedOut] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorType, setErrorType] = useState<'generic' | 'access_denied' | null>(null);
  const [userClaims, setUserClaims] = useState<any | null>(null);
  const [apiUser, setApiUser] = useState<ApiUser | null>(null);
  const [apiUserLoading, setApiUserLoading] = useState(false);
  const [apiUserError, setApiUserError] = useState<string | null>(null);

  // Check for explicit signout flag on initialization
  useEffect(() => {
    const explicitSignout = sessionStorage.getItem('msal_explicit_signout');
    if (explicitSignout) {
      setUserSignedOut(true);
    }

    // Clear explicit signout flag when page is unloaded (new session)
    const handleBeforeUnload = () => {
      // Only clear if user is not currently authenticated
      // This allows auto-signin to work again in new sessions
      if (!isAuthenticated) {
        sessionStorage.removeItem('msal_explicit_signout');
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isAuthenticated]);

  // Decode JWT token to extract claims (including roles)
  useEffect(() => {
    const extractUserClaims = async () => {
      if (!isAuthenticated || !account) {
        setUserClaims(null);
        return;
      }

      try {
        const request = {
          scopes: loginRequest.scopes,
          account: account,
          forceRefresh: false
        };

        const response = await instance.acquireTokenSilent(request);
        
        // For app roles, we need to check the ID token, not the access token
        // Azure AD includes app roles in the ID token
        let claimsToUse = null;
        
        if (response.idToken) {
          // Decode ID token to extract claims (includes app roles)
          const base64Url = response.idToken.split('.')[1];
          const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
          const jsonPayload = decodeURIComponent(
            atob(base64)
              .split('')
              .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
              .join('')
          );
          
          claimsToUse = JSON.parse(jsonPayload);
          console.log('[Auth] ID Token claims:', claimsToUse);
        }
        
        // Fallback to access token if ID token is not available
        if (!claimsToUse && response.accessToken) {
          const base64Url = response.accessToken.split('.')[1];
          const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
          const jsonPayload = decodeURIComponent(
            atob(base64)
              .split('')
              .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
              .join('')
          );
          
          claimsToUse = JSON.parse(jsonPayload);
          console.log('[Auth] Access Token claims (fallback):', claimsToUse);
        }
        
        if (claimsToUse) {
          setUserClaims(claimsToUse);
          console.log('[Auth] Available claim keys:', Object.keys(claimsToUse));
          console.log('[Auth] Roles claim:', claimsToUse.roles);
          console.log('[Auth] All role-related claims:', {
            roles: claimsToUse.roles,
            app_roles: claimsToUse.app_roles,
            wids: claimsToUse.wids,
            groups: claimsToUse.groups
          });
        }
      } catch (error) {
        console.error('[Auth] Failed to extract user claims:', error);
        setUserClaims(null);
      }
    };

    extractUserClaims();
  }, [instance, isAuthenticated, account]);

  // Auto-signin attempt using ssoSilent for true SSO experience
  useEffect(() => {
    const attemptAutoSignin = async () => {
      // Skip if already authenticated, login in progress, already attempted, or user explicitly signed out
      if (isAuthenticated || loginInProgress || autoSigninAttempted || userSignedOut || inProgress !== InteractionStatus.None) {
        return;
      }

      // Check if user explicitly signed out in this session
      const explicitSignout = sessionStorage.getItem('msal_explicit_signout');
      if (explicitSignout) {
        console.log('[Auth] Skipping auto sign-in due to explicit logout in this session');
        setAutoSigninAttempted(true);
        return;
      }

      console.log('[Auth] Attempting automatic sign-in...');
      setAutoSigninAttempted(true);
      setLoginInProgress(true);
      setError(null);
      setErrorType(null);

      try {
        // Method 1: Try ssoSilent without user hint first - most reliable for SSO
        const response = await instance.ssoSilent({
          ...loginRequest,
          prompt: 'none', // Ensure silent authentication
        });
        console.log('[Auth] Auto sign-in successful:', response);
        
        // Track successful auto login
        trackAuthEvent('login_success', {
          environment: import.meta.env.MODE,
          userAgent: navigator.userAgent,
          autoLogin: true
        });
      } catch (error) {
        console.log('[Auth] ssoSilent failed, trying alternative methods:', error);
        
        // Method 2: Try with existing accounts if available
        const accounts = instance.getAllAccounts();
        if (accounts.length > 0) {
          try {
            console.log('[Auth] Attempting auto sign-in with existing account...');
            const response = await instance.ssoSilent({
              ...loginRequest,
              account: accounts[0],
              prompt: 'none',
            });
            console.log('[Auth] Auto sign-in with account successful:', response);
            
            trackAuthEvent('login_success', {
              environment: import.meta.env.MODE,
              userAgent: navigator.userAgent,
              autoLogin: true,
              method: 'account_hint'
            });
            return; // Success, exit early
          } catch (accountError) {
            console.log('[Auth] Account-based auto sign-in also failed:', accountError);
          }
        }
        
        // Log the final failure but don't show error to user
        console.log('[Auth] All auto sign-in methods failed, user will need to sign in manually');
        
        // Only track as an error if it's not a typical "no session" case
        if (error instanceof AuthError) {
          const errorCode = error.errorCode;
          
          // Common expected errors that shouldn't be tracked as exceptions
          const expectedErrors = [
            'login_required',
            'interaction_required', 
            'no_account_in_silent_request',
            'monitor_window_timeout',
            'iframe_interaction_not_supported'
          ];
          
          if (!expectedErrors.includes(errorCode)) {
            trackException(new Error('Auto sign-in failed'), {
              errorCode: errorCode,
              component: 'auto_authentication',
              method: 'ssoSilent'
            });
          }
        }
      } finally {
        setLoginInProgress(false);
      }
    };

    // Only attempt auto-signin on initial load
    if (!autoSigninAttempted) {
      attemptAutoSignin();
    }
  }, [instance, isAuthenticated, loginInProgress, autoSigninAttempted, userSignedOut, inProgress]);

  // Set up the auth token provider for ChatAPI
  useEffect(() => {
    const getAccessToken = async (): Promise<string | null> => {
      if (!isAuthenticated || !account) {
        return null;
      }

      try {
        const request = {
          scopes: tokenRequest.scopes, // Use API-only scopes to avoid mixed-audience tokens
          account: account,
          forceRefresh: false
        };

        const response = await instance.acquireTokenSilent(request);
        return response.accessToken;
      } catch (error) {
        console.error('[Auth] Silent token acquisition failed:', error);
        try {
          const request = {
            scopes: tokenRequest.scopes, // Use API-only scopes to avoid mixed-audience tokens
            prompt: 'none'
          };
          const response = await instance.acquireTokenPopup(request);
          return response.accessToken;
        } catch (popupError) {
          console.error('[Auth] Popup token acquisition failed:', popupError);
          return null;
        }
      }
    };

    ChatAPI.setAuthProvider({
      getToken: getAccessToken
    });
  }, [instance, isAuthenticated, account]);

  useEffect(() => {
    let isCurrent = true;

    if (!isAuthenticated || !account || userSignedOut || inProgress !== InteractionStatus.None) {
      setApiUser(null);
      setApiUserLoading(false);
      setApiUserError(null);
      return () => {
        isCurrent = false;
      };
    }

    setApiUser(null);
    setApiUserLoading(true);
    setApiUserError(null);

    ChatAPI.getCurrentUser()
      .then((userInfo) => {
        if (isCurrent) {
          setApiUser(userInfo);
        }
      })
      .catch((fetchError: unknown) => {
        if (isCurrent) {
          setApiUserError(fetchError instanceof Error ? fetchError.message : 'Unable to load API user.');
        }
      })
      .finally(() => {
        if (isCurrent) {
          setApiUserLoading(false);
        }
      });

    return () => {
      isCurrent = false;
    };
  }, [account?.homeAccountId, inProgress, isAuthenticated, userSignedOut]);

  // Set up event callbacks
  useEffect(() => {
    const callbackId = instance.addEventCallback((event: EventMessage) => {
      console.log('[Auth] MSAL Event received:', event.eventType, event);
      
      if (event.eventType === EventType.LOGIN_SUCCESS) {
        console.log('[Security] Login Success Event:', {
          timestamp: new Date().toISOString(),
          userId: (event.payload as any)?.account?.homeAccountId || 'unknown',
          environment: import.meta.env.MODE
        });
        // Track successful login
        trackAuthEvent('login_success', {
          environment: import.meta.env.MODE,
          userAgent: navigator.userAgent
        });
        setError(null);
        setErrorType(null);
      } else if (event.eventType === EventType.LOGIN_FAILURE) {
        console.error('[Security] Login Failure Event:', {
          timestamp: new Date().toISOString(),
          error: (event.error as any)?.errorCode || event.error?.message,
          environment: import.meta.env.MODE
        });
        // Track failed login
        trackAuthEvent('login_failure', {
          error: (event.error as any)?.errorCode || event.error?.message,
          environment: import.meta.env.MODE
        });
        if (event.error) {
          const parsedError = parseAzureADError(event.error);
          setError(parsedError.message);
          setErrorType(parsedError.type);
          // Track the exception
          trackException(new Error(parsedError.message), {
            errorType: parsedError.type,
            component: 'authentication'
          });
        }
      } else if (event.eventType === EventType.LOGOUT_SUCCESS) {
        console.log('[Security] Logout Success Event:', {
          timestamp: new Date().toISOString(),
          environment: import.meta.env.MODE
        });
        
        // Mark that user explicitly signed out to prevent auto-signin
        // Only set this AFTER logout actually completes successfully
        console.log('[Auth] Setting explicit signout flag due to LOGOUT_SUCCESS event');
        sessionStorage.setItem('msal_explicit_signout', 'true');
        setUserSignedOut(true);
        
        // Track successful logout
        trackAuthEvent('logout', {
          environment: import.meta.env.MODE
        });
        setError(null);
        setErrorType(null);
      } else if (event.eventType === EventType.LOGOUT_FAILURE) {
        console.warn('[Security] Logout Failure Event:', {
          timestamp: new Date().toISOString(),
          error: (event.error as any)?.errorCode || event.error?.message,
          environment: import.meta.env.MODE
        });
        
        // Don't set signout flag if logout failed - user might have cancelled
        // This allows auto-signin to continue working if they didn't actually want to logout
        console.log('[Auth] Logout failed or was cancelled, not setting explicit signout flag');
      } else if (event.eventType.includes('logout')) {
        // Catch any other logout-related events
        console.log('[Auth] Other logout event detected:', event.eventType, event);
      } else if (event.eventType === EventType.ACQUIRE_TOKEN_FAILURE) {
        console.warn('[Security] Token Acquisition Failed:', {
          timestamp: new Date().toISOString(),
          error: (event.error as any)?.errorCode || event.error?.message,
          environment: import.meta.env.MODE
        });
        // Track token acquisition failure
        trackException(new Error('Token acquisition failed'), {
          error: (event.error as any)?.errorCode || event.error?.message,
          component: 'token_acquisition'
        });
      }
    });

    return () => {
      if (callbackId) {
        instance.removeEventCallback(callbackId);
      }
    };
  }, [instance]);

  /**
   * Login using popup
   */
  const login = async (): Promise<void> => {
    if (loginInProgress || inProgress !== InteractionStatus.None) {
      return;
    }

    setLoginInProgress(true);
    setError(null);
    setErrorType(null);

    try {
      const response: AuthenticationResult = await instance.loginPopup(loginRequest);
      console.log('Login successful:', response);
      
      // Clear explicit signout flag when user manually logs in
      sessionStorage.removeItem('msal_explicit_signout');
      setUserSignedOut(false);
    } catch (error) {
      console.error('Login failed:', error);
      if (error instanceof AuthError) {
        const parsedError = parseAzureADError(error);
        setError(parsedError.message);
        setErrorType(parsedError.type);
      } else {
        setError('Login failed. Please try again.');
        setErrorType('generic');
      }
    } finally {
      setLoginInProgress(false);
    }
  };

  /**
   * Logout
   */
  const logout = async (): Promise<void> => {
    console.log('[Auth] Starting logout process...');
    
    // Check accounts before logout
    const accountsBeforeLogout = instance.getAllAccounts();
    console.log('[Auth] Accounts before logout:', accountsBeforeLogout.length);
    
    try {
      // Don't set the signout flag yet - wait for logout to actually complete
      // The LOGOUT_SUCCESS event will handle setting the flag
      await instance.logoutPopup({
        postLogoutRedirectUri: window.location.origin,
        mainWindowRedirectUri: window.location.origin,
      });
      console.log('[Auth] Logout popup completed successfully');
      
      // Fallback check in case events don't fire properly
      setTimeout(() => {
        const accountsAfterLogout = instance.getAllAccounts();
        console.log('[Auth] Accounts after logout:', accountsAfterLogout.length);
        
        if (accountsBeforeLogout.length > 0 && accountsAfterLogout.length === 0) {
          console.log('[Auth] Fallback: Logout detected via account count change');
          if (!sessionStorage.getItem('msal_explicit_signout')) {
            sessionStorage.setItem('msal_explicit_signout', 'true');
            setUserSignedOut(true);
          }
        }
      }, 2000);
      
    } catch (error) {
      console.error('[Auth] Logout popup failed:', error);
      
      // Check if it's a popup error but logout might have actually worked
      const errorMessage = (error as any)?.message || '';
      const errorCode = (error as any)?.errorCode || '';
      
      if (errorCode === 'popup_window_error' || errorMessage.includes('popup')) {
        console.log('[Auth] Popup error detected, checking if user is actually logged out...');
        
        // Wait a moment for potential logout to complete
        setTimeout(() => {
          const accounts = instance.getAllAccounts();
          console.log('[Auth] Account check after popup error:', accounts.length);
          if (accounts.length === 0) {
            console.log('[Auth] User appears to be logged out despite popup error');
            // Set signout flag since logout seems to have succeeded
            sessionStorage.setItem('msal_explicit_signout', 'true');
            setUserSignedOut(true);
          } else {
            console.log('[Auth] User still has active accounts, logout likely failed');
          }
        }, 1000);
      } else {
        // Only set signout flag for actual logout failures (not popup issues)
        console.log('[Auth] Logout failed with non-popup error, setting signout flag');
        sessionStorage.setItem('msal_explicit_signout', 'true');
        setUserSignedOut(true);
        instance.clearCache();
        window.location.reload();
      }
    }
  };

  /**
   * Get access token for API calls
   */
  const getToken = async (): Promise<string | null> => {
    // Check if user explicitly signed out or if there are no accounts
    if (userSignedOut || sessionStorage.getItem('msal_explicit_signout')) {
      console.log('[Auth] getToken: User has explicitly signed out, returning null');
      return null;
    }

    if (!account) {
      console.log('[Auth] getToken: No account available, returning null');
      return null;
    }

    // Check if user is actually authenticated
    if (!isAuthenticated) {
      console.log('[Auth] getToken: User not authenticated, returning null');
      return null;
    }

    console.log('[Auth] getToken: Requesting token with scopes:', tokenRequest.scopes);

    try {
      const response = await instance.acquireTokenSilent({
        ...tokenRequest, // Use tokenRequest for API calls
        account: account,
      });
      console.log('[Auth] getToken: Silent token acquisition successful');
      console.log('[Auth] getToken: Token scopes:', response.scopes);
      return response.accessToken;
    } catch (error) {
      console.error('[Auth] Token acquisition failed:', error);
      
      // Check if it's because user session no longer exists (after logout)
      const errorCode = (error as any)?.errorCode;
      if (errorCode === 'interaction_required' && userSignedOut) {
        console.log('[Auth] Token acquisition failed due to logout, returning null');
        return null;
      }
      
      // If silent token acquisition fails, try interactive acquisition
      try {
        console.log('[Auth] getToken: Trying interactive token acquisition');
        const response = await instance.acquireTokenPopup(tokenRequest); // Use tokenRequest
        console.log('[Auth] getToken: Interactive token acquisition successful');
        console.log('[Auth] getToken: Token scopes:', response.scopes);
        return response.accessToken;
      } catch (interactiveError) {
        console.error('[Auth] Interactive token acquisition failed:', interactiveError);
        return null;
      }
    }
  };

  const contextValue: AuthContextType = {
    isAuthenticated,
    user: account,
    userClaims,
    apiUser,
    apiUserLoading,
    apiUserError,
    loginInProgress: loginInProgress || inProgress !== InteractionStatus.None,
    login,
    logout,
    getToken,
    error,
    errorType,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Main AuthProvider that wraps both MSAL and custom auth logic
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  return (
    <MsalProvider instance={msalInstance}>
      <AuthProviderInner>
        {children}
      </AuthProviderInner>
    </MsalProvider>
  );
};

export default AuthProvider;
